/**
 * Shared mount registries.
 *
 * Every SDX framework module stores its mounted component
 * instances and loaded component constructors in the same two
 * Maps, so sdx_reactive and other cross-framework consumers
 * can read them without caring which framework mounted which
 * component. The registry publishes itself on Drupal.sdx so
 * the handoff is observable from any script.
 *
 * The class is generic over the native instance type (React
 * Root, Vue app, Svelte component) so each framework keeps
 * its native instance references without type casting.
 */

import type { MountRegistries } from './types';

export class MountRegistry<Instance> implements MountRegistries<Instance> {
  readonly mountedRoots = new Map<Element, Instance>();
  readonly loadedComponents = new Map<string, unknown>();

  /**
   * Exposes the two maps on Drupal.sdx so sdx_reactive,
   * sdx_devel, and any other cross-framework consumer can
   * reach them. Safe to call repeatedly; the first call wins
   * for each page lifetime.
   */
  publishToDrupalNamespace(): void {
    if (typeof Drupal === 'undefined') return;
    const sdxNamespace = Drupal.sdx ?? {};
    sdxNamespace.mountedRoots = this.mountedRoots;
    sdxNamespace.loadedComponents = this.loadedComponents;
    Drupal.sdx = sdxNamespace;
  }
}
